import { useState } from 'react';
import { motion, useTransform } from 'framer-motion';
import { Plus } from 'lucide-react';
import type { Passbook } from '@/shared/types';
import { cn } from '@/shared/lib/cn';
import { SIDEBAR_WIDTH_EXPANDED } from '@/shared/lib/sidebar-collapsed';
import { useSidebarMotion } from '@/shared/lib/sidebar-motion';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { useChannelQuotas } from '../model/use-channel-quotas';
import type { QuotaChannel } from '../model/ledger';

import { RechargeDialog } from './recharge-dialog';
import {
  formatCount,
  quotaLimit,
  quotaSignal,
  remainingPercent,
  type AccountCapacity,
  type QuotaSignal,
} from '@/features/marketing-shared';

function meterColor(signal: QuotaSignal): string {
  return signal === 'approaching' ? 'bg-warning' : 'bg-primary';
}

function ringColor(signal: QuotaSignal): string {
  return signal === 'approaching' ? 'hsl(var(--warning))' : 'hsl(var(--primary))';
}

function balanceTitle(label: string, quota: AccountCapacity): string {
  const name = label.toLowerCase();
  if (quota.available <= 0) return `Sem saldo de ${name}`;
  return `Saldo de ${name}: ${formatCount(quota.available)} de ${formatCount(quotaLimit(quota))}`;
}

// Geometria do medidor (px). Aberto: rótulo em cima e barra embaixo, na largura
// útil da sidebar (268 − 2 de borda − 30 de padding). No trilho: o anel de 40px.
const BAR_LENGTH = SIDEBAR_WIDTH_EXPANDED - 32;
const RING_SIZE = 40;
const RING_STROKE = 3;
const BAR_STROKE = 6;
const RING_RADIUS = RING_SIZE / 2 - RING_STROKE / 2;
const OPEN_HEIGHT = 28;
const BAR_Y = OPEN_HEIGHT - BAR_STROKE / 2;

const lerp = (from: number, to: number, t: number) => from + (to - from) * t;
const px = (n: number) => n.toFixed(2);

/**
 * Traço do medidor em `u` (0 = barra, 1 = anel). A barra se enrola como fita: a
 * ponta direita curva para baixo num arco do tamanho do anel, que corre para a
 * esquerda enrolando o resto até fechar o círculo. O início do traço vira o topo
 * do anel, então o preenchimento segue começando ali e girando no sentido
 * horário — o mesmo desenho da barra, que enche a partir da esquerda.
 */
function meterPath(u: number): string {
  const startX = lerp(BAR_STROKE / 2, RING_SIZE / 2, u);
  const tipX = lerp(BAR_LENGTH - BAR_STROKE / 2, RING_SIZE / 2, u);
  const tipY = lerp(BAR_Y, RING_STROKE / 2, 1 - (1 - u) ** 2);
  const angle = 2 * Math.PI * u;
  let d = `M ${px(startX)} ${px(tipY)} L ${px(tipX)} ${px(tipY)}`;
  if (angle > 0.001) {
    // Arco a partir do topo do círculo (centro logo abaixo da ponta), em duas
    // metades: um arco só de 360° começa e termina no mesmo ponto e não desenha.
    const cx = tipX;
    const cy = tipY + RING_RADIUS;
    const at = (a: number) => `${px(cx + RING_RADIUS * Math.sin(a))} ${px(cy - RING_RADIUS * Math.cos(a))}`;
    d += ` A ${RING_RADIUS} ${RING_RADIUS} 0 0 1 ${at(angle / 2)} A ${RING_RADIUS} ${RING_RADIUS} 0 0 1 ${at(angle)}`;
  }
  return d;
}

function RemainingMeter({
  label,
  quota,
  onOpen,
}: {
  label: string;
  quota: AccountCapacity;
  onOpen: () => void;
}) {
  const { progress, reveal, rail, animated } = useSidebarMotion();
  const d = useTransform(progress, (p) => meterPath(1 - p));
  const strokeWidth = useTransform(progress, [0, 1], [RING_STROKE, BAR_STROKE]);
  const height = useTransform(progress, [0, 1], [RING_SIZE, OPEN_HEIGHT]);
  const left = remainingPercent(quota);
  const signal = quotaSignal(quota);
  const empty = quota.available <= 0;
  const title = balanceTitle(label, quota);

  const amount = empty ? (
    <span className="text-xs text-muted-foreground">Sem saldo</span>
  ) : (
    <span className={cn('text-xs font-semibold tabular-nums', signal === 'approaching' ? 'text-warning' : 'text-foreground')}>
      {formatCount(quota.available)}
    </span>
  );

  // Drawer mobile: sempre aberto, não morfa — barra simples na largura da tela.
  if (!animated) {
    return (
      <div className="space-y-1.5" title={title}>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs font-medium">{label}</span>
          {amount}
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          {empty ? null : (
            <div
              className={cn('h-full rounded-full transition-[width] duration-500', meterColor(signal))}
              style={{ width: `${left}%` }}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <motion.button
      type="button"
      title={title}
      aria-label={title}
      className="relative block w-full text-left"
      style={{ height }}
      onClick={onOpen}
    >
      <motion.span
        className="absolute inset-x-0 top-0 flex items-baseline justify-between gap-2 whitespace-nowrap"
        style={{ opacity: reveal }}
      >
        <span className="text-xs font-medium">{label}</span>
        {amount}
      </motion.span>
      <svg
        aria-hidden
        width={BAR_LENGTH}
        height={RING_SIZE}
        fill="none"
        strokeLinecap="round"
        className="pointer-events-none absolute left-0 top-0 overflow-visible"
      >
        <motion.path d={d} strokeWidth={strokeWidth} stroke="hsl(var(--muted))" />
        {empty ? null : (
          <motion.path
            d={d}
            strokeWidth={strokeWidth}
            stroke={ringColor(signal)}
            initial={false}
            animate={{ pathLength: left / 100 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        )}
      </svg>
      <motion.span
        className={cn(
          'absolute left-0 top-0 grid size-10 place-items-center text-[10px] font-semibold tabular-nums',
          empty ? 'text-muted-foreground' : 'text-foreground',
        )}
        style={{ opacity: rail }}
      >
        {empty ? '0' : left}
      </motion.span>
    </motion.button>
  );
}

function MeterSkeleton({ collapsed }: { collapsed?: boolean }) {
  if (collapsed) return <Skeleton className="size-10 rounded-full" />;
  return (
    <div className="space-y-1.5" aria-hidden>
      <div className="flex items-center justify-between">
        <Skeleton className="h-3 w-12 rounded" />
        <Skeleton className="h-3 w-10 rounded" />
      </div>
      <Skeleton className="h-1.5 w-full rounded-full" />
    </div>
  );
}

export function SidebarQuota({
  passbook,
  passbookId,
  collapsed,
}: {
  passbook?: Passbook | null;
  passbookId?: string;
  collapsed?: boolean;
}) {
  const emailOn = passbook?.hasEmailMarketing === true;
  const smsOn = passbook?.hasSmsMarketing === true;
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState<QuotaChannel>('email');
  // Só o que veio do servidor: o saldo-semente da demonstração fazia a barra nascer cheia e esvaziar.
  const { data, isPending } = useChannelQuotas(emailOn || smsOn ? passbookId : undefined);
  const { progress, reveal } = useSidebarMotion();
  const headingHeight = useTransform(progress, [0, 1], [0, 16]);
  // Sem o título no trilho, o gap de 12px que ele deixaria some junto.
  const headingGap = useTransform(progress, [0, 1], [-12, 0]);
  // Recarregar: botão largo aberto, quadrado de 32px centrado na coluna no trilho.
  const buttonWidth = useTransform(progress, (p) => `calc(32px + (100% - 32px) * ${p})`);
  const buttonInset = useTransform(progress, [0, 1], [4, 0]);
  const buttonLabelWidth = useTransform(progress, [0, 1], [0, 72]);
  const buttonLabelGap = useTransform(progress, [0, 1], [0, 8]);

  if (!passbookId || (!emailOn && !smsOn)) return null;

  const enabled = [
    emailOn ? { channel: 'email' as const, label: 'E-mail' } : null,
    smsOn ? { channel: 'sms' as const, label: 'SMS' } : null,
  ].filter((row) => row != null);
  const channels = enabled.map((row) => row.channel);

  const openRecharge = (next: QuotaChannel) => {
    setChannel(next);
    setOpen(true);
  };

  return (
    <div className="mb-3 flex flex-col gap-3" data-sidebar-hold={open || undefined}>
      <motion.p
        className="overflow-hidden whitespace-nowrap text-[11px] font-medium uppercase leading-4 tracking-wider text-muted-foreground"
        style={{ height: headingHeight, marginBottom: headingGap, opacity: reveal }}
      >
        Saldo
      </motion.p>
      {isPending
        ? enabled.map((row) => <MeterSkeleton key={row.channel} collapsed={collapsed} />)
        : data
          ? enabled.map((row) => (
              <RemainingMeter
                key={row.channel}
                label={row.label}
                quota={data[row.channel]}
                onOpen={() => openRecharge(row.channel)}
              />
            ))
          : collapsed
            ? null
            : <p className="text-xs text-muted-foreground">Saldo indisponível agora.</p>}
      <motion.div style={{ width: buttonWidth, marginLeft: buttonInset }}>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="w-full gap-0 px-0"
          onClick={() => openRecharge(channels[0])}
        >
          <Plus className="text-primary" />
          <motion.span
            className="overflow-hidden"
            style={{ maxWidth: buttonLabelWidth, marginLeft: buttonLabelGap, opacity: reveal }}
          >
            Recarregar
          </motion.span>
        </Button>
      </motion.div>
      <RechargeDialog open={open} onOpenChange={setOpen} passbookId={passbookId} channel={channel} channels={channels} />
    </div>
  );
}
